import React, {useEffect, useMemo, useRef, useState} from 'react';
import {createFragmentContainer, graphql} from 'react-relay';
import {FlexColumn, FlexRow, Label, Modal, Text} from '@forecast-it/design-system';
import {FormattedMessage} from 'react-intl';
import type {ExportInvoiceBillingOptionModal_viewer$data as ExportInvoiceBillingOptionModal_viewer} from './__generated__/ExportInvoiceBillingOptionModal_viewer.graphql';
import {MODAL_TYPE, showModal} from '../../../../../forecast-app/shared/components/modals/generic_modal_conductor';
import ConditionalDropdown from '../../../../../components/dropdowns/conditional-dropdown/ConditionalDropdown';
import {getNotNullishNodes} from '../../../../../forecast-app/shared/util/NotNullPredicate';
import CreateProjectBillToMutation from '../../../../../mutations/invoicing/CreateProjectBillToMutation';
import {CommitMutation} from '../../../../../mutations/CommitMutation';
import type {CreateProjectBillToMutation$data as CreateProjectBillToMutationResponse} from '../../../../../mutations/invoicing/__generated__/CreateProjectBillToMutation.graphql';
import InvoicingIntegrationIcon from '../../../../../forecast-app/admin-tab/finance-page/InvoicingIntegrationIcon';

type ExportInvoiceBillingOptionModalProps = {
	invoiceId: string;
	closeModal: () => Promise<void>;
	viewer: ExportInvoiceBillingOptionModal_viewer;
};

type Invoice = NonNullable<ExportInvoiceBillingOptionModal_viewer['invoice']>;
type Projects = NonNullable<Invoice['projects']>;
type Project = NonNullable<Projects[0]>;
type Client = NonNullable<Project['client']>;
type ProjectBillTo = NonNullable<NonNullable<NonNullable<NonNullable<Project['projectBillTos']>['edges']>[0]>['node']>;
type ClientBillTo = NonNullable<NonNullable<NonNullable<NonNullable<Client['clientBillTos']>['edges']>[0]>['node']>;
type BillFrom = NonNullable<ProjectBillTo['billFrom']>;
type ProjectOrClientBillTo = ProjectBillTo | ClientBillTo;

type ProjectBillToResponse = NonNullable<
	NonNullable<NonNullable<CreateProjectBillToMutationResponse['createProjectBillTo']>['projectBillTo']>['node']
>;

export const showExportIntegrationModal = (invoiceId: string, projectBillTo?: ProjectBillTo | ProjectBillToResponse | null) => {
	showModal({
		type: MODAL_TYPE.EXPORT_INVOICE,
		invoiceId,
		billToId: projectBillTo?.billTo?.id,
		integration: projectBillTo?.billFrom?.integration,
	});
};

const BillFromItem = ({billFrom}: {billFrom?: BillFrom}) => (
	<FlexRow>
		<InvoicingIntegrationIcon integration={billFrom?.integration} />
		<Text>{billFrom?.name}</Text>
	</FlexRow>
);
const BillToItem = ({billTo}: {billTo?: ProjectOrClientBillTo}) => (
	<FlexRow>
		<Text>{billTo?.billTo?.name}</Text>
	</FlexRow>
);

const getEligibleProjectBillTos = (projects?: Projects | null) => {
	const eligibleProjectBillTos = new Map<string, ProjectBillTo>();
	let firstProject = true;

	if (!projects) {
		return [];
	}
	for (const project of projects) {
		const projectBillTos = getNotNullishNodes(project?.projectBillTos?.edges);
		if (firstProject) {
			for (const projectBillTo of projectBillTos) {
				if (projectBillTo.billTo) {
					eligibleProjectBillTos.set(projectBillTo.billTo.id, projectBillTo);
				}
			}
		} else {
			// Any project bill to that doesn't use an already eligible bill to, should be removed.
			const projectBillToIds = new Set(projectBillTos.map(projectBillTo => projectBillTo.billTo?.id));
			for (const eligibleBillToId of eligibleProjectBillTos.keys()) {
				if (!projectBillToIds.has(eligibleBillToId)) {
					eligibleProjectBillTos.delete(eligibleBillToId);
				}
			}
		}

		firstProject = false;
	}
	return Array.from(eligibleProjectBillTos.values());
};

const getBillFromOptions = (billTos: ProjectBillTo[]) => {
	const existingBillFroIds = new Set();
	const billFromOptions: BillFrom[] = [];
	for (const billTo of billTos) {
		const billFrom = billTo.billFrom;
		if (billFrom) {
			if (!existingBillFroIds.has(billFrom.id)) {
				billFromOptions.push(billFrom);
				existingBillFroIds.add(billFrom.id);
			}
		}
	}
	return billFromOptions;
};

export const ExportInvoiceBillingOptionModal = ({viewer, invoiceId, closeModal}: ExportInvoiceBillingOptionModalProps) => {
	const invoice = viewer.invoice;
	const projects = invoice?.projects;
	const singleProject = projects?.length === 1;
	const project = singleProject ? projects?.[0] : undefined;
	const client = project ? project.client : undefined;

	const projectBillTos = useMemo<ProjectBillTo[]>(() => getEligibleProjectBillTos(projects), [viewer]);
	const clientBillTos = useMemo<ClientBillTo[]>(() => getNotNullishNodes(client?.clientBillTos?.edges), [viewer]);
	const billTos = (projectBillTos.length ? projectBillTos : clientBillTos) || [];
	const addProjectBillTo = projectBillTos.length === 0;
	const onlyOneProjectBillingOption = clientBillTos.length === 1;

	/*
	 * Saving new billing details hands over from two places: the save's own callback, and the saved
	 * details arriving through the store, which makes the project's one bill-to eligible below. Only
	 * the first may close this window and open the Export window, or two would stack, and closing the
	 * top one would show the untouched one beneath.
	 */
	const handedOver = useRef(false);
	const openExportModal = (projectBillTo?: ProjectBillTo | ProjectBillToResponse | null) => {
		if (handedOver.current) {
			return;
		}
		handedOver.current = true;
		closeModal().then(() => showExportIntegrationModal(invoiceId, projectBillTo));
	};

	useEffect(() => {
		const numOfProjectBillTos = projectBillTos.length;
		if (numOfProjectBillTos === 1) {
			openExportModal(projectBillTos[0]);
		} else if (numOfProjectBillTos === 0) {
			if (singleProject) {
				const hasClientBillTos = clientBillTos.length > 0;
				if (!hasClientBillTos) {
					closeModal().then(() =>
						showModal({
							type: MODAL_TYPE.MISSING_CLIENT_BILL_TO_WARNING,
							clientId: client?.id,
						})
					);
				}
			} else {
				closeModal().then(() =>
					showModal({
						type: MODAL_TYPE.PROJECTS_BILLING_DETAILS_WARNING,
					})
				);
			}
		}
	}, [closeModal, projectBillTos, clientBillTos]);

	const billFromOptions = useMemo<BillFrom[]>(() => getBillFromOptions(billTos), [projectBillTos, clientBillTos]);

	const [selectedBillFrom, setSelectedBillFrom] = useState<BillFrom>();
	const billToOptions = useMemo<ProjectOrClientBillTo[]>(
		() => (selectedBillFrom ? billTos.filter(billTo => billTo?.billFrom?.id === selectedBillFrom?.id) : []),
		[selectedBillFrom]
	);

	const [selectedBillTo, setSelectedBillTo] = useState<ProjectOrClientBillTo>();

	const doExport = () => {
		if (addProjectBillTo) {
			CommitMutation(
				CreateProjectBillToMutation,
				{
					billToId: selectedBillTo?.billTo?.id,
					clientBillToId: selectedBillTo?.id,
					clientId: client?.id,
					projectId: project?.id,
					billFromId: selectedBillFrom?.id,
				},
				addedProjectBillTo => {
					openExportModal(addedProjectBillTo.createProjectBillTo?.projectBillTo?.node);
				}
			);
		} else {
			openExportModal(selectedBillTo);
		}
	};

	return (
		<Modal onCloseModal={closeModal}>
			<Modal.Title>
				<FormattedMessage
					id={addProjectBillTo ? 'invoicing.add_billing_details_and_export_invoice' : 'invoicing.export_invoice'}
				/>
			</Modal.Title>
			<Modal.Content>
				<FormattedMessage
					id={
						singleProject
							? addProjectBillTo
								? onlyOneProjectBillingOption
									? 'invoicing.add_project_billing_details_one_pair'
									: 'invoicing.add_project_billing_details_multiple_pair'
								: 'invoicing.choose_project_billing_option'
							: 'invoicing.choose_projects_billing_option'
					}
				/>
				<FlexColumn gap="xs">
					<Label>
						<FormattedMessage id="common.bill_from" />
					</Label>
					<ConditionalDropdown
						options={billFromOptions}
						renderOption={billFrom => <BillFromItem billFrom={billFrom} />}
						onSelect={setSelectedBillFrom}
					>
						<BillFromItem billFrom={selectedBillFrom} />
					</ConditionalDropdown>
				</FlexColumn>
				<FlexColumn gap="xs">
					<Label>
						<FormattedMessage id="common.bill_to" />
					</Label>
					<ConditionalDropdown
						options={billToOptions}
						renderOption={item => <BillToItem billTo={item} />}
						onSelect={setSelectedBillTo}
					>
						<BillToItem billTo={selectedBillTo} />
					</ConditionalDropdown>
				</FlexColumn>
			</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton onPress={doExport} disabled={!selectedBillFrom || !selectedBillTo}>
					<FormattedMessage id={'common.continue'} />
				</Modal.PrimaryFooterButton>
				<Modal.SecondaryFooterButton onPress={closeModal}>
					<FormattedMessage id={'common.cancel'} />
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};

export const ExportInvoiceBillingOptionModalQuery = graphql`
	query ExportInvoiceBillingOptionModalQuery($invoiceId: ID!) {
		viewer {
			actualPersonId
			component(name: "ExportInvoiceBillingOptionModal")
			...ExportInvoiceBillingOptionModal_viewer @arguments(invoiceId: $invoiceId)
		}
	}
`;

export default createFragmentContainer(ExportInvoiceBillingOptionModal, {
	viewer: graphql`
		fragment ExportInvoiceBillingOptionModal_viewer on Viewer @argumentDefinitions(invoiceId: {type: "ID!"}) {
			id
			invoice(id: $invoiceId) {
				projects {
					id
					client {
						id
						clientBillTos(first: 10000) @connection(key: "Client_clientBillTos", filters: []) {
							edges {
								node {
									id
									billTo {
										id
										name
									}
									billFrom {
										id
										name
										integration
									}
								}
							}
						}
					}
					projectBillTos(first: 10000) @connection(key: "Project_projectBillTos", filters: []) {
						edges {
							node {
								id
								billTo {
									id
									name
								}
								billFrom {
									id
									name
									integration
								}
							}
						}
					}
				}
			}
		}
	`,
});
